import { Controller } from "@hotwired/stimulus";
import { Turbo } from "@hotwired/turbo-rails";
import { birthLunarDay } from "../lib/lunar_day";

const DEFAULT_TIME = "12:00";
const DEFAULT_SITE = "riga";

// Birth-day personalisation, entirely on the device. The birth date, time and city are
// kept only in this browser's localStorage (as JSON); they are turned into a lunar day
// 1..30 here, and only that number is ever put on the URL / sent to the server.
//
//   data-controller="birth-day"
//   data-birth-day-current-value="<the birth_day param, or ''>"
//   data-birth-day-provenance-value="<'computed for %{city}, %{time}' in the current locale>"
//   <input data-birth-day-target="date" type="date">
//   <input data-birth-day-target="time" type="time">
//   <select data-birth-day-target="site">
//   <span data-birth-day-target="provenance"> (optional — filled in client-side)
//   <button data-action="birth-day#forget">Forget</button>
export default class extends Controller {
  static targets = ["date", "time", "site", "provenance"];
  static values = { current: String, provenance: String };

  static STORAGE_KEY = "pirtsDayBirthDate";

  connect() {
    const stored = this.#stored();
    if (!stored.date || !this.hasDateTarget) return;

    this.#hydrate(stored);
    // A returning visitor with a saved date but no ?birth_day= yet — resolve it once.
    if (!this.currentValue) this.#applyDate(stored, { replace: true });
    else this.#updateProvenance(stored);
  }

  submit() {
    if (!this.hasDateTarget) return;
    const date = this.dateTarget.value;
    if (!date) return;

    const time = this.hasTimeTarget ? this.timeTarget.value : DEFAULT_TIME;
    const site = this.hasSiteTarget ? this.siteTarget.value : DEFAULT_SITE;
    this.#applyDate({ date, time, site });
  }

  forget() {
    this.#store(null);
    this.#clear();
    this.#visit(this.#urlWithout("birth_day"));
  }

  #hydrate({ date, time, site }) {
    if (this.hasDateTarget) this.dateTarget.value = date;
    if (this.hasTimeTarget) this.timeTarget.value = time;
    if (this.hasSiteTarget) this.siteTarget.value = site;
  }

  #clear() {
    if (this.hasDateTarget) this.dateTarget.value = "";
    if (this.hasTimeTarget) this.timeTarget.value = DEFAULT_TIME;
    if (this.hasSiteTarget) this.siteTarget.value = DEFAULT_SITE;
  }

  #applyDate({ date, time, site }, { replace = false } = {}) {
    const day = birthLunarDay(date, time, site);
    if (!day) return;
    const normalised = { date, time: time || DEFAULT_TIME, site: site || DEFAULT_SITE };
    this.#store(normalised);
    this.#updateProvenance(normalised);
    this.#visit(this.#urlWith("birth_day", day), replace);
  }

  #updateProvenance({ time, site }) {
    if (!this.hasProvenanceTarget || !this.provenanceValue) return;

    const city = this.hasSiteTarget ? this.siteTarget.selectedOptions[0]?.text : site;
    this.provenanceTarget.textContent = this.provenanceValue
      .replace("%{city}", city ?? site)
      .replace("%{time}", time);
  }

  // A full Turbo Drive visit — the address bar always follows (only the number is on
  // the URL). A frame-scoped visit doesn't reliably promote the URL, and changing the
  // birth day is a deliberate, infrequent navigation, so the extra chrome repaint is
  // fine.
  #visit(url, replace = false) {
    Turbo.visit(url, { action: replace ? "replace" : "advance" });
  }

  #urlWith(key, value) {
    const url = new URL(window.location.href);
    url.searchParams.set(key, value);
    return url.toString();
  }

  #urlWithout(key) {
    const url = new URL(window.location.href);
    url.searchParams.delete(key);
    return url.toString();
  }

  #stored() {
    const raw = this.#read();
    if (!raw) return { date: "", time: DEFAULT_TIME, site: DEFAULT_SITE };

    let parsed;
    try {
      parsed = JSON.parse(raw);
    } catch {
      parsed = { date: raw }; // legacy bare-string value
    }
    return {
      date: parsed?.date ?? "",
      time: parsed?.time || DEFAULT_TIME,
      site: parsed?.site || DEFAULT_SITE,
    };
  }

  #read() {
    try {
      return window.localStorage.getItem(this.constructor.STORAGE_KEY);
    } catch {
      return null;
    }
  }

  #store(value) {
    try {
      if (value) window.localStorage.setItem(this.constructor.STORAGE_KEY, JSON.stringify(value));
      else window.localStorage.removeItem(this.constructor.STORAGE_KEY);
    } catch {
      /* private mode / storage blocked — the feature just won't be remembered */
    }
  }
}
